import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { DataTable } from '../../../../shared/components/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';

interface DuplicatePair {
  adId1: string;
  adId2: string;
  jobNumber1: string | null;
  jobNumber2: string | null;
  mediumCategory: string | null;
  vendorName1: string | null;
  vendorName2: string | null;
  sameVendor: boolean;
  distanceMeters: number;
  hoursApart: number;
}
interface DuplicateResult {
  radiusMeters: number;
  timeWindowHours: number;
  criteria: string;
  total: number;
  pairs: DuplicatePair[];
}
interface FlaggedReading {
  readingId: string;
  jobNumber: string | null;
  capturedAt: string | null;
  source: string;
  accuracyM: number | null;
  reasons: string[];
}
interface GpsResult {
  accuracyThresholdMeters: number;
  coordinateReuseThreshold: number;
  summary: { totalReadings: number; accuracyExceeded: number; mockProvider: number; missingCoordinates: number; reusedCoordinateGroups: number };
  flaggedReadings: FlaggedReading[];
  reusedCoordinates: Array<{ latitude: number; longitude: number; count: number }>;
  note: string;
}

/** §12 duplicate detection + §13 GPS validation. */
export function GeoQualityScreen(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  // Instants follow the tenant's DISPLAY_DATE_FORMAT, not the reader's language picker.
  const { at } = useBusinessDay();
  const [dup, setDup] = useState<DuplicateResult | null>(null);
  const [gps, setGps] = useState<GpsResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    Promise.all([
      apiRequest<{ data: DuplicateResult }>('/v1/geo-intel/duplicates', { token }),
      apiRequest<{ data: GpsResult }>('/v1/geo-intel/gps-validation', { token }),
    ])
      .then(([d, g]) => { if (!alive) return; setDup(d.data); setGps(g.data); setError(null); })
      .catch((e) => {
        if (!alive) return;
        setError(e instanceof ApiError && e.status === 403
          ? t('m16report.quality.forbidden')
          : (e as Error).message);
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token]);

  return (
    <main aria-label="geo-quality">
      <h1>{t('m16report.quality.title')}</h1>
      {error && <p role="alert" aria-label="quality-error">{error}</p>}
      {loading ? <p>{t('common.loading')}</p> : (
        <>
          {dup && (
            <section aria-label="duplicates">
              <h2>{t('m16report.quality.dup.title')}</h2>
              <p aria-label="dup-criteria">{t('m16report.quality.dup.criteria', { radius: dup.radiusMeters, window: dup.timeWindowHours, total: dup.total })}</p>
              <DataTable
                ariaLabel="duplicates-table"
                emptyMessage={t('m16report.quality.dup.empty')}
                rowKey={(p) => `${p.adId1}-${p.adId2}`}
                rows={dup.pairs}
                columns={[
                  { key: 'adA', header: t('m16report.quality.dup.col.adA'), render: (p) => p.jobNumber1 ?? '—' },
                  { key: 'adB', header: t('m16report.quality.dup.col.adB'), render: (p) => p.jobNumber2 ?? '—' },
                  { key: 'medium', header: t('m16report.col.medium'), render: (p) => p.mediumCategory ?? '—' },
                  { key: 'distance', header: t('m16report.quality.dup.col.distance'), render: (p) => p.distanceMeters },
                  { key: 'hours', header: t('m16report.quality.dup.col.hours'), render: (p) => p.hoursApart },
                  { key: 'sameVendor', header: t('m16report.quality.dup.col.sameVendor'), render: (p) => (p.sameVendor ? t('m16report.quality.yes') : t('m16report.quality.no')) },
                ]}
              />
            </section>
          )}

          {gps && (
            <section aria-label="gps-validation">
              <h2>{t('m16report.quality.gps.title')}</h2>
              <p aria-label="gps-summary">
                {t('m16report.quality.gps.summary', { total: gps.summary.totalReadings, accuracy: gps.accuracyThresholdMeters, exceeded: gps.summary.accuracyExceeded, mock: gps.summary.mockProvider, reused: gps.summary.reusedCoordinateGroups })}
              </p>
              <DataTable
                ariaLabel="gps-flags-table"
                emptyMessage={t('m16report.quality.gps.empty')}
                rowKey={(r) => r.readingId}
                rows={gps.flaggedReadings}
                columns={[
                  { key: 'reading', header: t('m16report.quality.gps.col.reading'), render: (r) => [r.jobNumber, r.capturedAt ? at(r.capturedAt) : null].filter(Boolean).join(' · ') || '—' },
                  { key: 'source', header: t('m16report.quality.gps.col.source'), render: (r) => r.source },
                  { key: 'accuracy', header: t('m16report.quality.gps.col.accuracy'), render: (r) => r.accuracyM ?? '—' },
                  { key: 'reasons', header: t('m16report.quality.gps.col.reasons'), render: (r) => r.reasons.join('; ') },
                ]}
              />
              <p aria-label="gps-note">{gps.note}</p>
            </section>
          )}
        </>
      )}
    </main>
  );
}
